import { Instance } from 'mobx-state-tree';
import * as React from 'react';
import { observer, inject } from 'mobx-react';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import { TLCode } from '../../common/enums';
import styled from '@emotion/styled';
import TranslatedMessage from '../common/translation/TranslatedMessage';
import {
  PlannerStore,
  DutyExchangeStore,
  DutyAssignmentStore,
  SystemStatusStore,
  SecretariatStore,
  AuthenticationStore,
} from '../../store';
import { SystemStatus } from '../../store/models/SystemStatus';
import { Button, ButtonGroup, InputGroup, Intent, NumericInput, Position, Spinner, Tooltip } from '@blueprintjs/core';
import { getFirstMonday, getFirstSunday } from '../../common/utils';
import moment from 'moment';
import DateInput from '../common/input/DateInput';

interface DutyDownloadTabProps extends WithTranslateProps {
  secretariatId?: number;
  refresh: () => void;
  PlannerStore?: Instance<typeof PlannerStore>;
  SystemStatusStore?: Instance<typeof SystemStatusStore>;
  DutyExchangeStore?: Instance<typeof DutyExchangeStore>;
  DutyAssignmentStore?: Instance<typeof DutyAssignmentStore>;
  SecretariatStore?: Instance<typeof SecretariatStore>;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
}

interface DutyDownloadTabState {
  fileIsLoading?: boolean;
  isDownloading?: boolean;
  modalFileLoadingOpened: boolean;
  dutiesImportError: string[];
  year: number;
  startDatePlanning?: Date | undefined;
  endDatePlanning?: Date | undefined;
  firstSemesterDate?: string | undefined;
  lastSemesterDate?: string | undefined;
  selectedSemester: string;
  systemStatus?: Array<Instance<typeof SystemStatus>>;
  fileLinesLimit: number;
  exchangeExportIsEmpty?: boolean;
  assignmentExportIsEmpty?: boolean;
  geowachtStartDate?: Date | undefined;
}

const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  .bp3-button {
    margin-right: 3px;
  }
`;

const SpinnerContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

@inject('PlannerStore')
@inject('DutyExchangeStore', 'DutyAssignmentStore')
@inject('SystemStatusStore', 'AuthenticationStore', 'SecretariatStore')
@observer
class DutyDownloadTab extends React.Component<DutyDownloadTabProps, DutyDownloadTabState> {
  state = {
    fileIsLoading: false,
    isDownloading: false,
    modalFileLoadingOpened: false,
    dutiesImportError: [],
    year: new Date().getFullYear(),
    selectedSemester: '',
    startDatePlanning: undefined,
    endDatePlanning: undefined,
    fileLinesLimit: 2000,
    exchangeExportIsEmpty: false,
    assignmentExportIsEmpty: false,
    geowachtStartDate: new Date(),
  } as DutyDownloadTabState;

  _isMounted = false;

  async componentDidMount() {
    this._isMounted = true;
    const criteria: any = {
      subSystem: 'lastPublishedDuty',
      secretariatId: this.props.AuthenticationStore!.isSuperAdmin ? undefined : this.getSecretariatId(),
    };
    const systemStatus = await this.props.SystemStatusStore!.search(criteria);
    // @ts-ignore
    this.setState({ systemStatus });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  getSecretariatId = () => {
    if (this.props.AuthenticationStore!.isSuperAdmin)
      return this.props.SecretariatStore!.selectedSecretariatId === 0
        ? undefined
        : this.props.SecretariatStore!.selectedSecretariatId;
    if (this.props.AuthenticationStore!.isSecretariat) return this.props.AuthenticationStore!.secretariatId;
    return undefined;
  };

  getTextWarningDateLastPublishedDuty = () => {
    const secretariatId = this.getSecretariatId();
    if (this.state.systemStatus) {
      if (this.props.AuthenticationStore!.isSuperAdmin && secretariatId === undefined) {
        const momentDates = this.state.systemStatus.map((status) => moment(status.status, 'DD/MM/YYYY'));
        const minDate = moment.min(momentDates);
        return (
          this.props.translate(TLCode.DUTYDOWNLOAD_WarningSecretariatDownLoadDuties_SuperAdmin) +
          ' ' +
          minDate.format('DD/MM/YYYY')
        );
      }
      if (this.props.AuthenticationStore!.isSuperAdmin && secretariatId !== undefined) {
        const status = this.state.systemStatus.find(
          (status) => secretariatId && status.secretariatId === secretariatId,
        );
        if (status)
          return (
            this.props.translate(TLCode.DUTYDOWNLOAD_WarningSecretariatDownLoadDuties_SuperAdmin) +
            ' ' +
            moment(status['status'], 'DD/MM/YYYY').format('DD/MM/YYYY')
          );
        return this.props.translate(TLCode.DUTYDOWNLOAD_WarningSecretariatDownLoadDuties_SuperAdmin) + ' ?';
      }
      if (this.props.AuthenticationStore!.isSecretariat)
        return (
          this.props.translate(TLCode.DUTYDOWNLOAD_WarningSecretariatDownLoadDuties_Admin) +
          ' ' +
          moment(this.state.systemStatus[0]['status'], 'DD/MM/YYYY').format('DD/MM/YYYY')
        );
    }
    return '';
  };

  uploadDutiesOnGeowacht = async () => {
    await this.props.DutyAssignmentStore!.uploadDutiesOnGeowacht();
  };

  geowachtDownloadDutyAssignments = async (data) => {
    this._isMounted && this.setState({ isDownloading: true });

    const d = this.state.geowachtStartDate!;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');

    const from = `${yyyy}-${mm}-${dd}T00:00:00`;

    await this.props.PlannerStore!.geowachtDownloadPlanning({
      from,
      secretariatId: this.getSecretariatId(),
    });

    this._isMounted && this.setState({ isDownloading: false });
  };

  onGeowachtStartDateChange = async (date) => {
    this.setState({ geowachtStartDate: date });
  };

  downloadDutyAssignments = async (data) => {
    this._isMounted && this.setState({ isDownloading: true });
    console.log(this.state.startDatePlanning);
    console.log(this.state.endDatePlanning);
    await this.props.PlannerStore!.downloadPlanning({
      from: this.state.firstSemesterDate!,
      to: this.state.lastSemesterDate!,
      fileLinesLimit: this.state.fileLinesLimit,
      secretariatId: this.getSecretariatId(),
    });
    this._isMounted && this.setState({ isDownloading: false });
  };

  onYearChange = (valueAsNumber: number, valueAsString: string) => {
    this.setState({ year: valueAsNumber });
    if (this.state.selectedSemester === 'S1') this.setS1();
    if (this.state.selectedSemester === 'S2') this.setS2();
  };

  setS1 = () => {
    let date = moment();
    date.set('year', this.state.year);
    date.set('month', 0);
    date.set('date', 1);
    this.setState({ startDatePlanning: date.toDate() });
    date.set('month', 6);
    date.set('date', 0);
    this.setState({ endDatePlanning: date.toDate() });
    const firstDate = getFirstMonday(this.state.year, 0);
    this.setState({ firstSemesterDate: moment(firstDate).format('YYYY-MM-DD') });
    const lastDate = getFirstSunday(this.state.year, 6);
    this.setState({ lastSemesterDate: moment(lastDate).format('YYYY-MM-DD') });
    this.setState({ selectedSemester: 'S1' });
  };

  setS2 = () => {
    let date = moment();
    date.set('year', this.state.year);
    date.set('month', 6);
    date.set('date', 1);
    this.setState({ startDatePlanning: date.toDate() });
    date.set('month', 12);
    date.set('date', 0);
    this.setState({ endDatePlanning: date.toDate() });
    const firstDate = getFirstMonday(this.state.year, 6);
    this.setState({ firstSemesterDate: moment(firstDate).format('YYYY-MM-DD') });
    const lastDate = getFirstSunday(this.state.year + 1, 0);
    this.setState({ lastSemesterDate: moment(lastDate).format('YYYY-MM-DD') });
    this.setState({ selectedSemester: 'S2' });
  };

  onFileLinesLimitChange = (valueAsNumber: number, valueAsString: string) => {
    this.setState({ fileLinesLimit: valueAsNumber });
  };

  render() {
    const { isDownloading } = this.state;
    const { translate } = this.props;
    return (
      <Container>
        &nbsp;
        {isDownloading ? (
          <>
            <SpinnerContainer>
              <Spinner intent={Intent.PRIMARY} size={Spinner.SIZE_STANDARD} />
            </SpinnerContainer>
          </>
        ) : (
          <>
            <DivLine>
              <span style={{ color: 'red' }}>
                <b>
                  <TranslatedMessage id={TLCode.ROSTER_Geowacht} />
                </b>
              </span>
              &nbsp;
              <TranslatedMessage id={TLCode.ROSTER_GeowachtDownloadDutyAssignments} />
              &nbsp;
              <DateInput
                value={this.state.geowachtStartDate}
                onChange={this.onGeowachtStartDateChange}
                disabled={true}
              />
              &nbsp;
              <ButtonGroup>
                <Button intent="primary" onClick={this.geowachtDownloadDutyAssignments}>
                  <TranslatedMessage id={TLCode.ROSTER_DownloadDutyAssignmentsButton} />
                </Button>
              </ButtonGroup>
            </DivLine>
            <br />
            <br />
            <DivLine>
              <span>{this.getTextWarningDateLastPublishedDuty()}</span>
            </DivLine>
            <br />
            <DivLine>
              <span style={{ color: 'red' }}>
                <b>
                  <TranslatedMessage id={TLCode.ROSTER_Geowacht} />
                </b>
              </span>
              &nbsp;
              <TranslatedMessage id={TLCode.ROSTER_UploadDutyAssignmentsLabel} />
              &nbsp;
              <ButtonGroup>
                <Button intent="primary" onClick={this.uploadDutiesOnGeowacht}>
                  <TranslatedMessage id={TLCode.ROSTER_UploadDutyAssignmentsButton} />
                </Button>
              </ButtonGroup>
            </DivLine>{' '}
          </>
        )}
      </Container>
    );
  }
}

export default withTranslate(DutyDownloadTab);
